import { useEffect, useState } from "react";
import { TaskIconResolveResponseSchema, type TaskIconId } from "@oyako-flow/contracts";
import { createOyakoFlowClient } from "../../../clients/typescript/src/index";

interface IconTask {
  task: { id: string; title: string; child_copy: string };
  assignment: { id: string };
  project: { name: string; child_copy: string } | null;
}
function signature(item: IconTask): string {
  return JSON.stringify([
    item.task.title,
    item.task.child_copy,
    item.project?.name,
    item.project?.child_copy,
  ]);
}
export function useTaskIcons(workspaceId: string, sessionId: string, tasks: IconTask[]) {
  const [resolved, setResolved] = useState<{
    binding: string;
    icons: Record<string, { signature: string; icon: TaskIconId }>;
  }>({ binding: "", icons: {} });
  const binding = `${workspaceId}:${sessionId}`;
  // Dates, task state and versions deliberately do not restart selection.
  const subjects = JSON.stringify(
    tasks.map((item) => ({
      task_id: item.task.id,
      assignment_id: item.assignment.id,
      signature: signature(item),
    })),
  );
  useEffect(() => {
    const items = JSON.parse(subjects) as {
      task_id: string;
      assignment_id: string;
      signature: string;
    }[];
    if (!items.length) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const client = createOyakoFlowClient("", async (input, init) => {
      const request = new Request(input, init);
      const cookie = document.cookie
        .split(";")
        .map((s) => s.trim())
        .find((s) => s.startsWith("oyako_csrf="));
      if (cookie)
        request.headers.set("X-CSRF-Token", decodeURIComponent(cookie.slice("oyako_csrf=".length)));
      return fetch(request, { credentials: "same-origin", signal: controller.signal });
    });
    async function load(round = 0): Promise<void> {
      let pending = false;
      try {
        for (let offset = 0; offset < items.length; offset += 40) {
          const chunk = items.slice(offset, offset + 40);
          const response = await client.POST(
            "/api/v1/workspaces/{workspaceId}/task-icons/resolve",
            {
              params: { path: { workspaceId } },
              body: {
                items: chunk.map(({ task_id, assignment_id }) => ({ task_id, assignment_id })),
              },
            },
          );
          if (controller.signal.aborted || !response.data) return;
          const data = TaskIconResolveResponseSchema.parse(response.data);
          const updates: Record<string, { signature: string; icon: TaskIconId }> = {};
          for (const icon of data.items) {
            pending ||= icon.status === "pending";
            const source = chunk.find(
              (item) => item.assignment_id === icon.assignment_id && item.task_id === icon.task_id,
            );
            if (source && icon.status === "ready")
              updates[icon.assignment_id] = { signature: source.signature, icon: icon.icon_id };
          }
          setResolved((old) => ({
            binding,
            icons: { ...(old.binding === binding ? old.icons : {}), ...updates },
          }));
        }
        if (pending && round < 5 && !controller.signal.aborted)
          timer = setTimeout(() => void load(round + 1), 3000);
      } catch {
        /* Decoration stays available during network or provider failures. */
      }
    }
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [binding, subjects, workspaceId]);
  return (item: IconTask): TaskIconId => {
    const entry = resolved.binding === binding ? resolved.icons[item.assignment.id] : undefined;
    return entry?.signature === signature(item) ? entry.icon : "general";
  };
}
